// src/scripts/calendar/ui/ModalContentGenerator.js
/**
 * Centralized Modal Content Generator
 * Single source of truth for all event modal content generation
 */

import { escapeHtml } from '../../utils/dom.js';

export default class ModalContentGenerator {
  constructor(options = {}) {
    this.monthNames = options.monthNames || [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    this.typeColors = options.typeColors || {};

    // Config cache for event descriptions
    // typeMap: { EventType: 'category' } — fetched from /api/calendar/type-map
    // configs: { category: { eventTitle: { description, ... } } }
    this.configCache = {
      typeMap: {},
      configs: {},
      loaded: false
    };

    // Loading promise to prevent race conditions
    this._loadingPromise = null;
  }

  /**
   * Load config files for event descriptions
   * Fetches and caches config files from server via API
   * Uses a loading promise to prevent race conditions from concurrent calls
   */
  async _loadConfigs() {
    // Already loaded - return immediately
    if (this.configCache.loaded) {
      return;
    }

    // If already loading, wait for that promise
    if (this._loadingPromise) {
      return this._loadingPromise;
    }

    // Start loading and store the promise
    this._loadingPromise = this._doLoadConfigs();

    try {
      await this._loadingPromise;
    } finally {
      this._loadingPromise = null;
    }
  }

  /**
   * Internal method that performs the actual config loading.
   * Fetches EVENT_TYPE_TO_CATEGORY from the server (derived from AllowedEvents.json),
   * then fetches each category's config — no hardcoded type/category lists here.
   */
  async _doLoadConfigs() {
    try {
      const typeMapRes = await fetch('/api/calendar/type-map').catch(() => null);
      const typeMap = typeMapRes?.ok ? await typeMapRes.json() : {};
      this.configCache.typeMap = typeMap;

      const categories = [...new Set(Object.values(typeMap))];
      const results = await Promise.all(
        categories.map(cat => fetch(`/api/calendar/config/${cat}`).catch(() => null))
      );

      categories.forEach((cat, i) => {
        this.configCache.configs[cat] = results[i]?.ok ? results[i].json().catch(() => ({})) : Promise.resolve({});
      });

      // Resolve all config promises
      const resolved = await Promise.all(categories.map(cat => this.configCache.configs[cat]));
      categories.forEach((cat, i) => {
        this.configCache.configs[cat] = resolved[i];
      });

      this.configCache.loaded = true;
    } catch {
      this.configCache.loaded = true;
    }
  }

  /**
   * Get description from config files based on event title and type.
   * Uses the server-provided type map — no hardcoded event type names.
   * @param {string} eventTitle - Event title/summary
   * @param {Array|string} eventTypes - Event type(s) from location.type
   * @returns {string} Description from config or empty string
   */
  _getConfigDescription(eventTitle, eventTypes) {
    if (!this.configCache.loaded || !eventTitle) {
      return '';
    }

    const types = Array.isArray(eventTypes) ? eventTypes : [eventTypes];

    for (const type of types) {
      const category = this.configCache.typeMap[type];
      const config = category && this.configCache.configs[category];
      if (!config) continue;

      // Try exact match first
      if (config[eventTitle]?.description) {
        return config[eventTitle].description;
      }

      // Try case-insensitive match
      const titleLower = eventTitle.toLowerCase();
      for (const [key, value] of Object.entries(config)) {
        if (key.toLowerCase() === titleLower && value.description) {
          return value.description;
        }
      }
    }

    return '';
  }

  /**
   * Get shortTitle from config for an event, loading configs if needed.
   * Returns null if no shortTitle is defined (caller should fall back to full summary).
   * @param {string} eventTitle - Event title/summary (Google Calendar name)
   * @param {Array|string} eventTypes - Event type(s) from locData.type
   * @returns {Promise<string|null>}
   */
  async getShortTitle(eventTitle, eventTypes) {
    await this._loadConfigs();

    if (!eventTitle) return null;

    const types = Array.isArray(eventTypes) ? eventTypes : [eventTypes];

    for (const type of types) {
      const category = this.configCache.typeMap[type];
      const config = category && this.configCache.configs[category];
      if (!config) continue;

      // Try exact match
      if (config[eventTitle]?.shortTitle) return config[eventTitle].shortTitle;

      // Try case-insensitive match
      const titleLower = eventTitle.toLowerCase();
      for (const [key, value] of Object.entries(config)) {
        if (key.toLowerCase() === titleLower && value.shortTitle) {
          return value.shortTitle;
        }
      }
    }

    return null;
  }

  /**
   * Main method - generates modal content for any event source
   * @param {Object} event - Event object from any source
   * @param {string} dateText - Optional date text override
   * @returns {Promise<string>} HTML content for modal
   */
  async generateModalContent(event, dateText = '') {
    if (!event) {
      return this._generateErrorContent('No event data available');
    }

    try {
      // Load configs if not already loaded
      await this._loadConfigs();

      const normalizedEvent = this._normalizeEvent(event);
      return this._buildModalHTML(normalizedEvent, dateText);
    } catch (error) {
      console.error('Error generating modal content:', error);
      return this._generateErrorContent('Error loading event details');
    }
  }

  /**
   * Normalize event from different sources into consistent format
   */
  _normalizeEvent(event) {
    // Extract event types from location data
    const eventTypes = this._extractEventTypes(event);

    // Handle already-normalized events (from calendarViewService)
    if (event.display) {
      const title = event.display.modalTitle || event.summary || 'Untitled Event';
      const locData = event.locData || null;
      const customDescription = locData?.customDescription || null;
      // Use full description; fall back to descSum (summarized) only if description is absent
      let description = event.display.modalDescription || event.description || event.descSum || '';

      // Fallback to config description if event description is empty
      if (!customDescription && (!description || description.trim() === '')) {
        description = this._getConfigDescription(title, eventTypes);
      }

      return {
        id: event.id || null,
        recurringEventId: event.recurringEventId || null,
        title,
        customDescription,
        description,
        location: locData,
        startDate: new Date(event.start.dateTime || event.start.date),
        endDate: new Date(event.end?.dateTime || event.end?.date || event.start.dateTime || event.start.date),
        extendedProps: event.extendedProps || {}
      };
    }

    // Handle FullCalendar events
    const title = event.title || event.summary || 'Untitled Event';
    const locData = event.locData || (typeof event.extendedProps?.location === 'object' ? event.extendedProps.location : null);
    const customDescription = locData?.customDescription || event.extendedProps?.locData?.customDescription || null;
    // Use full description; fall back to descSum (summarized) only if description is absent
    let description = event.description || event.descSum || event.extendedProps?.description || event.extendedProps?.descSum || '';

    // Fallback to config description if event description is empty
    if (!customDescription && (!description || description.trim() === '')) {
      description = this._getConfigDescription(title, eventTypes);
    }

    return {
      id: event.id || event.extendedProps?.id || null,
      recurringEventId: event.recurringEventId || event.extendedProps?.recurringEventId || null,
      title,
      customDescription,
      description,
      location: event.location || event.extendedProps?.location || null,
      startDate: event.start ? new Date(event.start) : null,
      endDate: event.end ? new Date(event.end) : null,
      extendedProps: event.extendedProps || {}
    };
  }

  /**
   * Extract event types from location data
   * @param {Object} event - Event object
   * @returns {Array} Array of event types
   */
  _extractEventTypes(event) {
    // Try locData first (server format)
    if (event.locData?.type) {
      return Array.isArray(event.locData.type) ? event.locData.type : [event.locData.type];
    }

    // Try location object
    let location = event.location || event.extendedProps?.location;
    if (typeof location === 'string') {
      try {
        location = JSON.parse(location);
      } catch (e) {
        return [];
      }
    }

    if (location?.type) {
      return Array.isArray(location.type) ? location.type : [location.type];
    }

    return [];
  }

  /**
   * Build the complete modal HTML
   */
  _buildModalHTML(event, dateText) {
    const sections = [
      this._buildTitleSection(event.title),
      this._buildDateTimeSection(event.startDate, event.endDate, dateText),
      this._buildLocationSection(event.location),
      this._buildDescriptionSection(event.customDescription || event.description, !!event.customDescription),
      this._buildRegistrationSection(event.location, event.title),
      this._buildLearnMoreSection(event.location, event.recurringEventId || event.id)
    ].filter(Boolean);

    return `
      <div class="event-modal-content">
        ${sections.join('\n')}
      </div>
    `;
  }

  /**
   * Individual section builders
   */
  _buildTitleSection(title) {
    return `<h2 class="event-title">${escapeHtml(title)}</h2>`;
  }

  _buildDateTimeSection(startDate, endDate, dateText) {
    if (dateText) {
      return `
        <div class="event-date">
          <strong>📅 Date:</strong> ${escapeHtml(dateText)}
        </div>
      `;
    }

    if (!startDate) return '';

    const dateStr = startDate.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    let timeZone;
    try {
      timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (_) {
      timeZone = undefined;
    }

    const timeOptions = { hour: 'numeric', minute: '2-digit', hour12: true, timeZone };

    const tzAbbr = timeZone
      ? (new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
          .formatToParts(startDate)
          .find(p => p.type === 'timeZoneName')?.value || '')
      : '';

    const timeStr = startDate.toLocaleTimeString('en-US', timeOptions);

    let html = `
      <div class="event-date-time">
        <strong>📅 Date:</strong> ${dateStr}<br>
        <strong>🕐 Time:</strong> ${timeStr}
    `;

    if (endDate && endDate.getTime() !== startDate.getTime()) {
      const endTimeStr = endDate.toLocaleTimeString('en-US', timeOptions);
      html += ` - ${endTimeStr}`;
    }

    if (tzAbbr) {
      html += ` ${tzAbbr}`;
    }

    html += '</div>';
    return html;
  }

  _buildLocationSection(location) {
    if (!location) return '';

    // Handle string location data
    if (typeof location === 'string') {
      try {
        location = JSON.parse(location);
      } catch (e) {
        // Treat as plain text location
        return `
          <div class="event-location">
            <strong>📍 Location:</strong> ${escapeHtml(location)}
          </div>
        `;
      }
    }

    // Handle object location data
    if (typeof location === 'object' && location !== null) {
      let html = '<div class="event-location">';
      let hasContent = false;

      // Display event type
      if (location.type) {
        const typeDisplay = Array.isArray(location.type)
          ? location.type.join(', ')
          : location.type;
        html += `<strong>👥 Event:</strong> ${escapeHtml(typeDisplay)}<br>`;
        hasContent = true;
      }

      // Display series session count (only when noEvents is set and event is not ongoing)
      if (location.noEvents != null && !location.ongoing) {
        html += `<strong>🔢 Series:</strong> ${escapeHtml(String(location.noEvents))} Sessions<br>`;
        hasContent = true;
      }

      // Display format (Hybrid shown as "Virtual or In-Person", etc.) with optional modality
      if (location.format) {
        const formatLabel = location.format === 'Hybrid' ? 'Hybrid (Virtual or In-Person)' : location.format;
        const formatDisplay = location.modality
          ? `${escapeHtml(formatLabel)} (${escapeHtml(location.modality)})`
          : escapeHtml(formatLabel);
        html += `<strong>📋 Format:</strong> ${formatDisplay}<br>`;
        hasContent = true;
      }

      // Display price (price:0 means Free; use explicit null/undefined check)
      const hasPrice = location.price != null;
      const hasSeries = location.priceSeries != null;
      if (hasPrice || hasSeries) {
        html += `<strong>💲 Price:</strong> `;

        if (hasPrice && location.price === 0) {
          html += 'Free';
        } else if (hasPrice && hasSeries) {
          html += `$${escapeHtml(location.price)} or $${escapeHtml(location.priceSeries)} for the series`;
        } else if (hasPrice) {
          html += `$${escapeHtml(location.price)}`;
        } else {
          html += `$${escapeHtml(location.priceSeries)}`;
        }

        html += '<br>';
        hasContent = true;
      }

      // Display facilitator (field may be named facilitator or lead)
      const facilitator = location.facilitator || location.lead;
      if (facilitator && facilitator.trim() !== '') {
        html += `<strong>⭐ Facilitator:</strong> ${escapeHtml(facilitator)}<br>`;
        hasContent = true;
      }

      html += '</div>';

      return hasContent ? html : '';
    }

    return '';
  }

  _buildDescriptionSection(description, isHtml = false) {
    if (!description) return '';

    return `
      <div class="event-description">
        <strong>📝 Description:</strong>
        <div class="description-content">
          ${isHtml ? description : this._formatDescription(description)}
        </div>
      </div>
    `;
  }

  /**
   * Format description text with basic markdown support
   */
  _formatDescription(description) {
    if (!description) return 'No description available';

    return escapeHtml(description)
      .replace(/\n/g, '<br>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>');
  }

  _buildEventAnchor(eventId) {
    if (!eventId) return '';
    const fullId = eventId.replace(/[^A-Za-z0-9_-]/g, '-');
    const sanitizedId = fullId.length > 5 ? fullId.slice(-5) : fullId;
    return `event-${sanitizedId}`;
  }

  _buildEventPageUrl(location, eventId) {
    if (!location || typeof location !== 'object') return null;
    const typeToPage = {
      'Workshop': '/events/workshops',
      'Training': '/events/training',
      'HHH': '/events/help-her-heal',
      'ICM': '/events/inner-child-model',
      'Group': '/services/groups',
    };
    const types = Array.isArray(location.type) ? location.type : [location.type].filter(Boolean);
    for (const type of types) {
      const page = typeToPage[type];
      if (page) {
        const anchor = this._buildEventAnchor(eventId);
        return anchor ? `${page}#${anchor}` : page;
      }
    }
    return null;
  }

  _buildLearnMoreSection(location, eventId) {
    const url = this._buildEventPageUrl(location, eventId);
    if (!url) return '';
    return `
      <div class="modal-learn-more">
        <a href="${escapeHtml(url)}" class="modal-learn-more-btn" target="_blank" rel="noopener noreferrer">Learn More</a>
      </div>
    `;
  }

  _buildRegistrationSection(location, title) {
    if (!location || typeof location !== 'object') return '';

    if (location.statusOverride?.status === 'closed') {
      const facilitator = location.facilitator || location.lead || '';
      return `<div class="modal-registration-buttons">
        <button type="button" class="modal-register-btn contact-us-btn" data-contact-trigger="true"
          data-modal-type="event-closed-notify"
          data-event-title="${escapeHtml(title || '')}"
          data-facilitator="${escapeHtml(facilitator)}"
          data-page-slug="events">
          Keep Me Posted?
        </button>
      </div>`;
    }

    const sessionUrl = location.sessionUrl;
    const seriesUrl = location.seriesUrl;

    if (!sessionUrl && !seriesUrl) return '';

    let buttons = '';
    if (sessionUrl) {
      buttons += `<a href="${escapeHtml(sessionUrl)}" class="modal-register-btn" target="_blank" rel="noopener noreferrer">Register for Session</a>`;
    }
    if (seriesUrl) {
      buttons += `<a href="${escapeHtml(seriesUrl)}" class="modal-register-btn modal-register-btn--series" target="_blank" rel="noopener noreferrer">Register for Series</a>`;
    }

    return `<div class="modal-registration-buttons">${buttons}</div>`;
  }

  /**
   * Generate error content for modal display
   */
  _generateErrorContent(message) {
    return `
      <div class="event-modal-content">
        <p class="error-message">${escapeHtml(message)}</p>
      </div>
    `;
  }
}